import { Stack, Typography } from "@mui/material";
import * as React from "react";

import { useSetRecoilState } from "recoil";
import { getValueOnBreakpoint } from "../../../lib/getValueOnBreakpoint";
import { clickHandler } from "../../../lib/clickHandler";

import { isEqual, uniq } from "lodash";

import {
  IXFooterProps,
  XFooterProps,
} from "../../../utils/interfaces/common/XFooter.interfaces";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useXoraiaValue } from "../../../hooks/useXoraia";
import { USE_SELECTED_INDEX } from "../../../hooks/useSelectedElement";
import { SECONDARY_COLOR } from "../../../utils/constants/common/utils";
import { CONTAINERS_NAME } from "../../../utils/constants/common/componentName";
import { useMount } from "react-use";
import { useXCommon, useXCommonUpdater } from "../../../hooks/useXCommon";
import XFooterCore from "./core";
import { TDynamicXElement } from "../../../utils/types/core/DynamicXElement";

const XStack = React.lazy(() => import("../../containers/XStack"));
const XGrid = React.lazy(() => import("../../containers/XGrid"));

const compareProps = (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
};

const XFooter: React.FC<XFooterProps> = React.memo(
  ({ initData = { all: {} } }) => {
    // WE TRACK THE BREAK POINTS CHANGES
    const breakpoint = useBreakpoint();

    const { env } = useXoraiaValue();

    const { xCommon, setXCommon } = useXCommon("FOOTER");

    useMount(() => {
      if (!xCommon) {
        setXCommon({
          name: "XFooter",
          childOf: null,
          elementsIndexes: [],
          hasChildren: true,
          hierachy: "XFooter",
          index: "FOOTER",
          value: initData,
        });
      }
    });

    // WE ARE GETTING THE CURRENT ELEMENT FROM THE ATOM FAMILY
    // const { xElement } = useXElement<TUseXElement<IXFooterProps>>(config.index);

    // GARBING THE INDEX UPDATER FOR GENERAL USAGE
    const setSelectedElement = useSetRecoilState(USE_SELECTED_INDEX);

    // IF WE DONT HAVE THIS ELEMENT CONFIGS ADDED WE DON'T SHOW IT
    // if (!xElement || Object.keys(xElement).length === 0) return null;

    const devProps = {
      id: "FOOTER",
      onClick: (e: any) => clickHandler(e, setSelectedElement, "FOOTER"),
    };

    if (!xCommon) {
      return (
        <Stack
          {...(env === "dev" && devProps)}
          sx={{
            width: "100%",
          }}
        >
          <Typography
            textAlign="center"
            py={1.5}
            fontWeight={600}
            variant="caption"
            color="GrayText"
            bgcolor="#EFEFEF"
            sx={{ "& a": { color: SECONDARY_COLOR, textDecoration: "none" } }}
          >
            Powered by{" "}
            <a href="https://xoraia.com" target="_blank">
              Xoraia
            </a>{" "}
            a baby of{" "}
            <a href="https://drosarl.com" target="_blank">
              DRO SARL
            </a>
          </Typography>
        </Stack>
      );
    }

    // WE GET ALL DEVICES PROPS AS VALUE
    const { value } = xCommon;

    // WE NOW USE THE CURRENT BREAKPOINT VALUE ON THIS ELEMENT
    const valueAtBreakPoint = getValueOnBreakpoint<IXFooterProps>(
      value,
      breakpoint
    );

    return (
      <XFooterCore
        index="FOOTER"
        props={valueAtBreakPoint}
        devProps={devProps}
        DynamicXElement={DynamicXElement}
      />
    );
  },
  compareProps
);

export default XFooter;

const DynamicXElement: TDynamicXElement = React.memo(
  ({ child, index, position }) => {
    const setParent = useXCommonUpdater(child.config.childOf ?? "NO_PARENT");

    const setFooter = useXCommonUpdater(index);

    React.useEffect(() => {
      setParent((old) => {
        if (!old) return null;

        const elementsIndexes = old.elementsIndexes
          ? [...old.elementsIndexes, index]
          : [index];

        return { ...old, elementsIndexes: uniq(elementsIndexes) };
      });

      setFooter({
        name: child.name,

        index: child.index ?? index,

        hierachy: child.hierachy ?? "XFooter-" + child.name,

        value: child.value ?? child.props ?? { all: {} },

        elementsIndexes: child.elementsIndexes ?? [],

        hasChildren: child.hasChildren ?? CONTAINERS_NAME.includes(child.name),

        childOf: child.childOf ?? child.config.childOf ?? null,
      });
    }, [index, position]);

    if (child.config.childOf !== "FOOTER") return null;

    switch (child.name) {
      case "XStack":
        return (
          <React.Suspense fallback={<p>Loading stack...</p>}>
            <XStack {...child} />
          </React.Suspense>
        );
      case "XGrid":
        return (
          <React.Suspense fallback={<p>loading grid...</p>}>
            <XGrid {...child} />
          </React.Suspense>
        );

      default:
        return null;
    }
  },
  compareProps
);
